import { ChromeOutlined, CloudServerOutlined, CodeOutlined, DatabaseOutlined, DownloadOutlined, PlayCircleFilled, RightOutlined, ThunderboltFilled } from "@ant-design/icons";
import { Button, Card, Col, Flex, Row, Space, Statistic, Steps, Typography } from "antd";
import styled from "styled-components";
import { ActionExecutor, type ActionExecutorHandler } from "../components/ActionExecutor";
import { useRef } from "react";
import { useNavigate } from "react-router";

const StyledComponent = styled(Flex)`
  .hero { min-height: 330px; padding: 42px; overflow: hidden; position: relative; background: linear-gradient(135deg, #171d28 0%, #121720 68%, #29251a 100%); }
  .hero::after { content: ''; position: absolute; width: 320px; height: 320px; right: -70px; top: -130px; border: 1px solid rgba(245,196,81,.2); border-radius: 50%; box-shadow: 0 0 0 42px rgba(245,196,81,.025), 0 0 0 84px rgba(245,196,81,.018); }
  .hero-copy { max-width: 720px; position: relative; z-index: 1; }
  .hero-title { max-width: 680px; margin: 12px 0 !important; font-size: clamp(36px, 5vw, 64px) !important; line-height: 1.04 !important; letter-spacing: -.045em; }
  .hero-description { max-width: 590px; font-size: 16px; line-height: 1.8; }
  .guide-card { padding: 12px 8px; }
  .guide-card .ant-steps-item-title { font-weight: 700; }
  .guide-card .ant-steps-item-description { max-width: 240px; line-height: 1.65; }
  .quick-link { height: 100%; transition: border-color .2s ease, transform .2s ease; }
  .quick-link:hover { transform: translateY(-2px); border-color: rgba(245,196,81,.35) !important; }
  .quick-link-icon { width: 42px; height: 42px; color: #f5c451; font-size: 20px; border-radius: 12px; background: rgba(245,196,81,.1); }
`;
export const IndexPage = () => {
  const executor = useRef<ActionExecutorHandler>(null);
  const navigate = useNavigate();
  const guideSteps = [
    { title: '安装执行节点', content: '下载并启动 Agent，用页面生成的 Agent ID 完成节点绑定。', icon: <CloudServerOutlined /> },
    { title: '创建浏览器会话', content: '为账号建立独立会话，登录状态会保存在对应的浏览器环境中。', icon: <ChromeOutlined /> },
    { title: '编排脚本与资源', content: '组合跳转、点击、输入和 JS 动作，并用资源变量管理动态数据。', icon: <CodeOutlined /> },
    { title: '执行并检查结果', content: '选择节点、会话和脚本开始运行，随后在运行记录中查看结果。', icon: <PlayCircleFilled /> },
  ];
  const quickLinks = [
    { title: '配置运行环境', description: '创建 Agent 和持久化浏览器会话', path: '/sessions', icon: <ChromeOutlined /> },
    { title: '创建自动化脚本', description: '按步骤编排浏览器操作流程', path: '/actions', icon: <CodeOutlined /> },
    { title: '维护资源变量', description: '统一管理脚本需要的动态参数', path: '/resources', icon: <DatabaseOutlined /> },
  ];
  return (
    <StyledComponent className="page-shell" vertical gap={24}>
      <Card className="hero surface-card" variant="outlined">
        <Flex className="hero-copy" vertical align="flex-start">
          <Typography.Text className="page-eyebrow"><ThunderboltFilled /> DISTRIBUTED AUTOMATION</Typography.Text>
          <Typography.Title className="hero-title">让每一个浏览器，成为可靠的执行节点。</Typography.Title>
          <Typography.Paragraph type="secondary" className="hero-description">集中管理代理、浏览器会话与自动化脚本，在保留登录状态的环境中安全、可视地执行任务。</Typography.Paragraph>
          <Space size={12} wrap>
            <Button type="primary" size="large" icon={<PlayCircleFilled />} onClick={() => executor.current?.open({})}>开始执行</Button>
            <Button size="large" icon={<DownloadOutlined />} href="https://openlist.ksr.la/@s/2PZ79tsv" target="_blank">下载 Agent</Button>
          </Space>
        </Flex>
      </Card>
      <Row gutter={[16,16]}>
        {[['执行节点','跨平台 Agent',<CloudServerOutlined />],['浏览器环境','持久化会话',<ChromeOutlined />],['自动化能力','可组合脚本',<CodeOutlined />]].map(([title,value,icon]) => <Col xs={24} md={8} key={String(title)}><Card className="surface-card"><Statistic title={title} value={String(value)} prefix={icon} /></Card></Col>)}
      </Row>
      <Flex className="page-header" vertical>
        <Typography.Text className="page-eyebrow">QUICK START</Typography.Text>
        <Typography.Title level={2} className="page-title">四步完成第一次自动化</Typography.Title>
        <Typography.Text type="secondary">系统由执行节点、浏览器会话、脚本和资源组成，按照下面的顺序配置即可开始。</Typography.Text>
      </Flex>
      <Card className="guide-card surface-card">
        <Steps responsive current={-1} items={guideSteps} />
      </Card>
      <Row gutter={[16, 16]}>
        {quickLinks.map(item => (
          <Col xs={24} lg={8} key={item.path}>
            <Card className="quick-link surface-card" hoverable onClick={() => navigate(item.path)}>
              <Flex align="center" gap={14}>
                <Flex className="quick-link-icon" align="center" justify="center">{item.icon}</Flex>
                <Flex vertical flex={1}>
                  <Typography.Text strong>{item.title}</Typography.Text>
                  <Typography.Text type="secondary">{item.description}</Typography.Text>
                </Flex>
                <RightOutlined />
              </Flex>
            </Card>
          </Col>
        ))}
      </Row>
      <ActionExecutor ref={executor} />
    </StyledComponent>
  );
}
